/**
 * templateOverlays.ts
 * Free-form text lines and images added on top of a rendered template page,
 * positioned absolutely (as % of the section box) so they survive re-renders
 * and export to PDF / print identically.
 *
 * Unlike `TemplateLayoutEdit` (which edits elements that already exist in the
 * DOCX, keyed by structural path), overlays are NEW elements that have no
 * stable path in the DOCX. They are stored as a separate list and re-injected
 * into every rendered `<section class="docx">` at capture/apply time.
 *
 * Positions are stored as percentages of the section's width/height so the
 * overlay lands in the same spot regardless of the paper size or zoom.
 */

export interface TemplateOverlay {
  /** Stable id so the same overlay can be moved/removed across re-renders. */
  id: string;
  kind: "text" | "image";
  /** Left edge as % of the section width (0–100). */
  x: number;
  /** Top edge as % of the section height (0–100). */
  y: number;
  /** Width as % of the section width (0–100). For text, the box width. */
  width?: number;
  /** Text content (kind === "text"). */
  text?: string;
  /** Font size in px (kind === "text"). */
  fontSize?: number;
  /** Font weight (kind === "text"). */
  fontWeight?: string;
  /** Text color (kind === "text"). */
  color?: string;
  /** Text alignment (kind === "text"). */
  textAlign?: "left" | "center" | "right";
  /** Base64 data URL of the image (kind === "image"). */
  dataUrl?: string;
  /** Stacking order. */
  zIndex?: number;
}

const OVERLAY_ATTR = "data-overlay-id";
const OVERLAY_CLASS = "template-overlay";

/** Marker injected into the section so capture can find overlays again. */
export const OVERLAY_MARKER_ATTR = "data-has-overlays";

function getSections(container: HTMLElement): HTMLElement[] {
  if (!container) return [];
  if (container.classList && container.classList.contains("docx")) return [container];
  return Array.from(container.querySelectorAll<HTMLElement>("section.docx"));
}

/** Read the numeric % value of an overlay element's left/top. */
function readPct(el: HTMLElement, prop: "left" | "top" | "width"): number {
  const v = parseFloat(el.style.getPropertyValue(prop));
  return Number.isFinite(v) ? v : 0;
}

/**
 * Serialize every overlay element currently rendered inside `container` into
 * a list of TemplateOverlay records. Call this after the user has added /
 * moved / edited overlays in the editor.
 */
export function captureTemplateOverlays(container: HTMLElement): TemplateOverlay[] {
  const out: TemplateOverlay[] = [];
  getSections(container).forEach((sec) => {
    sec.querySelectorAll<HTMLElement>(`[${OVERLAY_ATTR}]`).forEach((el) => {
      const id = el.getAttribute(OVERLAY_ATTR);
      if (!id) return;
      const kind = el.getAttribute("data-overlay-kind") as "text" | "image" | null;
      if (kind !== "text" && kind !== "image") return;

      const base: TemplateOverlay = {
        id,
        kind,
        x: readPct(el, "left"),
        y: readPct(el, "top"),
        width: readPct(el, "width"),
        zIndex: parseInt(el.style.zIndex || "0", 10) || 0,
      };

      if (kind === "text") {
        base.text = el.textContent ?? "";
        base.fontSize = parseFloat(el.style.fontSize) || 14;
        base.fontWeight = el.style.fontWeight || "normal";
        // Read the raw inline style so hex colors round-trip (jsdom normalizes
        // el.style.color to rgb()).
        const raw = el.getAttribute("style") ?? "";
        const colorMatch = /color:\s*([^;]+)/.exec(raw);
        base.color = colorMatch ? colorMatch[1].trim() : "#000000";
        base.textAlign = (el.style.textAlign as "left" | "center" | "right") || "left";
      } else {
        const img = el.querySelector("img");
        base.dataUrl = img?.getAttribute("src") ?? "";
      }
      out.push(base);
    });
  });
  return out;
}

/** Escape text for safe injection into an HTML string. */
function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Build the HTML string for one overlay, positioned absolutely inside a
 * `position:relative` section. Returns "" for an invalid overlay.
 */
export function overlayToHtml(o: TemplateOverlay): string {
  if (o.kind === "text") {
    const text = esc(o.text ?? "");
    const align = o.textAlign ?? "left";
    return (
      `<div ${OVERLAY_ATTR}="${esc(o.id)}" data-overlay-kind="text" class="${OVERLAY_CLASS}" ` +
      `style="position:absolute;left:${o.x}%;top:${o.y}%;width:${o.width ?? 100}%;` +
      `font-size:${o.fontSize ?? 14}px;font-weight:${o.fontWeight ?? "normal"};` +
      `color:${esc(o.color ?? "#000000")};text-align:${align};z-index:${o.zIndex ?? 0};` +
      `white-space:pre-wrap;line-height:1.2;pointer-events:none;">${text}</div>`
    );
  }
  if (o.kind === "image" && o.dataUrl) {
    return (
      `<div ${OVERLAY_ATTR}="${esc(o.id)}" data-overlay-kind="image" class="${OVERLAY_CLASS}" ` +
      `style="position:absolute;left:${o.x}%;top:${o.y}%;width:${o.width ?? 20}%;z-index:${o.zIndex ?? 0};` +
      `pointer-events:none;"><img src="${esc(o.dataUrl)}" style="width:100%;height:auto;display:block;" /></div>`
    );
  }
  return "";
}

/**
 * Inject overlays into a single rendered `<section class="docx">`. The section
 * is made `position:relative` so the absolute overlays anchor to it.
 */
export function applyTemplateOverlaysToSection(
  section: HTMLElement,
  overlays: TemplateOverlay[]
): void {
  if (!section) return;
  // Remove any previously injected overlays so re-applying is idempotent —
  // including when `overlays` is now empty (user removed them all).
  section.querySelectorAll<HTMLElement>(`[${OVERLAY_ATTR}]`).forEach((el) => el.remove());
  section.removeAttribute(OVERLAY_MARKER_ATTR);
  if (!overlays || overlays.length === 0) return;
  section.style.position = "relative";
  for (const o of overlays) {
    const html = overlayToHtml(o);
    if (!html) continue;
    const holder = document.createElement("div");
    holder.innerHTML = html;
    const node = holder.firstElementChild as HTMLElement | null;
    if (node) section.appendChild(node);
  }
  section.setAttribute(OVERLAY_MARKER_ATTR, "true");
}

/** Apply overlays to every section in a live container (editor preview). */
export function applyTemplateOverlaysToContainer(
  container: HTMLElement,
  overlays: TemplateOverlay[]
): void {
  if (!container) return;
  getSections(container).forEach((sec) => applyTemplateOverlaysToSection(sec, overlays));
}

/** Apply overlays to RenderedPage fragments before they are exported to PDF / print. */
export function applyTemplateOverlaysToPages<T extends { html: string }>(
  pages: T[],
  overlays: TemplateOverlay[]
): T[] {
  if (!overlays || overlays.length === 0) return pages;
  return pages.map((p) => {
    const holder = document.createElement("div");
    holder.innerHTML = p.html;
    const section = holder.querySelector<HTMLElement>("section.docx");
    if (section) {
      applyTemplateOverlaysToSection(section, overlays);
      return { ...p, html: section.outerHTML };
    }
    return p;
  });
}

/**
 * Remove any previously injected overlay nodes from RenderedPage html fragments.
 * Used to keep the export render cache free of overlays so overlay changes
 * (add/move/remove) always apply fresh and never go stale.
 */
export function stripTemplateOverlaysFromPages<T extends { html: string }>(
  pages: T[]
): T[] {
  return pages.map((p) => {
    if (!p.html.includes("data-overlay-id")) return p;
    const holder = document.createElement("div");
    holder.innerHTML = p.html;
    const section = holder.querySelector<HTMLElement>("section.docx");
    if (section) {
      section.querySelectorAll<HTMLElement>(`[${OVERLAY_ATTR}]`).forEach((el) => el.remove());
      section.removeAttribute(OVERLAY_MARKER_ATTR);
      return { ...p, html: section.outerHTML };
    }
    return p;
  });
}

/** Return a new overlay list with the overlay `id` removed (or unchanged if absent). */
export function removeOverlay(overlays: TemplateOverlay[], id: string): TemplateOverlay[] {
  return overlays.filter((o) => o.id !== id);
}

/** Return a new overlay list with a partial patch applied to the overlay `id`. */
export function updateOverlay(
  overlays: TemplateOverlay[],
  id: string,
  patch: Partial<TemplateOverlay>
): TemplateOverlay[] {
  return overlays.map((o) => (o.id === id ? { ...o, ...patch } : o));
}
